/**
 * Helper puro compartido (web + mobile) para el cálculo de fertilidad
 * histórica por animal (KAN-185 / KAN-186).
 *
 * Reglas de dominio:
 * - Un año cuenta como CONTROLADO si hay registro con preñez seteada
 *   (cualquier valor, incl. VACIA = tacto negativo real) o ternero al pie.
 * - Un año es EXITOSO si la preñez es P1/P2/P3 o la vaca tiene ternero al pie
 *   (VACIA + ternero = ya parió → cuenta a favor).
 * - Un año SIN dato (sin tacto, sin ternero) NO se calcula: no castiga.
 * - El estado actual solo aporta vía su fecha de último evento por campo
 *   (atribución correcta, no "año corriente").
 */

export interface PregnancyHistoryEntry {
  prenies?: string | null;
  tiene_ternero?: boolean | null;
  fecha_evento: string;
  fecha_creacion?: string;
}

export interface FertilityAnimalInput {
  prenies?: string | null;
  tiene_ternero?: boolean | null;
  prenies_historial?: PregnancyHistoryEntry[] | null;
  fecha_ultimo_evento_prenies?: string | null;
  fecha_ultimo_evento_tiene_ternero?: string | null;
}


export interface FertilityResult {
  /** Años con dato (controlados) */
  controlledYears: number;
  /** Años con preñez exitosa (P1/P2/P3 o ternero al pie) */
  successfulYears: number;
  /** % exitosos/controlados (0 si no hay años controlados), 2 decimales */
  fertilityRate: number;
  /** Años controlados, ordenados */
  years: number[];
}

const PREGNANT_STATES = ['P1', 'P2', 'P3'];

function isSet(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function yearOf(fecha?: string | null): number | null {
  if (!fecha) return null;
  const year = new Date(fecha).getFullYear();
  return Number.isFinite(year) && year > 1900 ? year : null;
}

export function calculateFertility(animal: FertilityAnimalInput): FertilityResult {
  const years = new Map<number, { controlled: boolean; successful: boolean }>();

  const mark = (year: number, controlled: boolean, successful: boolean) => {
    const entry = years.get(year) ?? { controlled: false, successful: false };
    entry.controlled = entry.controlled || controlled;
    entry.successful = entry.successful || successful;
    years.set(year, entry);
  };

  for (const record of animal.prenies_historial ?? []) {
    if (!record) continue;
    const year = yearOf(record.fecha_evento);
    if (year === null) continue;

    const preniesIsSet = isSet(record.prenies);
    const hasCalf = record.tiene_ternero === true;

    // Sin preñez registrada y sin ternero: no hubo tacto → el año no cuenta
    if (!preniesIsSet && !hasCalf) continue;

    mark(year, true, PREGNANT_STATES.includes(record.prenies as string) || hasCalf);
  }

  // Fallback: estado actual solo si el historial no aportó años, atribuido
  // al año de la fecha de último evento del campo (nunca al año corriente).
  if (years.size === 0) {
    if (isSet(animal.prenies)) {
      const year = yearOf(animal.fecha_ultimo_evento_prenies);
      if (year !== null) mark(year, true, PREGNANT_STATES.includes(animal.prenies as string));
    }
    if (animal.tiene_ternero === true) {
      const year = yearOf(animal.fecha_ultimo_evento_tiene_ternero);
      if (year !== null) mark(year, true, true);
    }
  }

  const sortedYears = [...years.keys()].sort((a, b) => a - b);
  const controlledYears = sortedYears.length;
  const successfulYears = [...years.values()].filter(y => y.successful).length;
  const fertilityRate =
    controlledYears > 0 ? Math.round((successfulYears / controlledYears) * 10000) / 100 : 0;

  return { controlledYears, successfulYears, fertilityRate, years: sortedYears };
}

export type FertilityStatus = 'green' | 'yellow' | 'red';

/**
 * Semáforo de la tasa de fertilidad histórica (>80 verde, ≥40 amarillo, sino
 * rojo). Compartido web + mobile: la web lo usaba inline en
 * DashboardCalculator.calculateFertilityData y el form de carga lo replica en
 * el panel de fertilidad de React Native.
 */
export function fertilityStatusFor(fertilityRate: number): FertilityStatus {
  if (fertilityRate > 80) return 'green';
  if (fertilityRate >= 40) return 'yellow';
  return 'red';
}

/** Parte de fecha YYYY-MM-DD de un string ISO (ignora la hora). null si no es parseable. */
function fechaSolo(fecha?: string | null): string | null {
  if (!fecha) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(fecha);
  return match ? match[1] : null;
}

/**
 * Meses de vigencia del estado de preñez y del ternero al pie, contados desde
 * la fecha de evento del registro (KAN-213: "la medición de preñez dura el
 * estado 8 meses; después de 8 meses no tiene ternero al pie"). La fecha que
 * manda es la del EVENTO, no la de carga: una importación histórica cargada
 * hoy con fecha_evento vieja no es vigente (KAN-214).
 */
export const MESES_VIGENCIA_PRENEZ = 8;

/** Estado de preñez + ternero vigentes a una fecha de corte, tras aplicar vencimiento. */
export interface EstadoPrenezVigente {
  /** 'P1' | 'P2' | 'P3' | 'VACIA' vigente al corte, o null si no hay dato vigente. */
  prenies: string | null;
  /** Ternero al pie vigente al corte (registro ≤ 8 meses); false si venció o no hay dato. */
  tiene_ternero: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `dia` (YYYY-MM-DD) menos `meses`, en YYYY-MM-DD. Aritmética local, sin UTC.
 * Con clamp de día: 31-may − 8 meses = 30-sep (no 01-oct por desborde). */
function fechaMenosMeses(dia: string, meses: number): string {
  const y = parseInt(dia.slice(0, 4), 10);
  const m = parseInt(dia.slice(5, 7), 10);
  const d = parseInt(dia.slice(8, 10), 10);
  const objetivo = new Date(y, m - 1 - meses, 1);
  const diasDelMes = new Date(objetivo.getFullYear(), objetivo.getMonth() + 1, 0).getDate();
  const diaFinal = Math.min(d, diasDelMes);
  return `${objetivo.getFullYear()}-${pad2(objetivo.getMonth() + 1)}-${pad2(diaFinal)}`;
}

/**
 * Último registro de `prenies_historial` con `fecha_evento ≤ hasta` que tenga
 * el campo pedido seteado, desempatando por fecha de creación. `campo`:
 * 'prenies' (valor no vacío) o 'ternero' (booleano no nulo).
 */
function ultimoRegistroConDato(
  historial: PregnancyHistoryEntry[],
  hastaDia: string,
  campo: 'prenies' | 'ternero'
): PregnancyHistoryEntry | null {
  let mejor: PregnancyHistoryEntry | null = null;
  let mejorDia: string | null = null;
  for (const entry of historial) {
    if (!entry) continue;
    const dia = fechaSolo(entry.fecha_evento);
    if (dia === null || dia > hastaDia) continue;
    const tieneDato =
      campo === 'prenies'
        ? isSet(entry.prenies)
        : entry.tiene_ternero !== undefined && entry.tiene_ternero !== null;
    if (!tieneDato) continue;
    const creada = entry.fecha_creacion ?? '';
    const mejorCreada = mejor?.fecha_creacion ?? '';
    if (mejorDia === null || dia > mejorDia || (dia === mejorDia && creada >= mejorCreada)) {
      mejor = entry;
      mejorDia = dia;
    }
  }
  return mejor;
}

/**
 * Estado de preñez y ternero al pie VIGENTES a la fecha de corte `hasta`
 * (YYYY-MM-DD), aplicando el vencimiento de 8 meses de KAN-213/214 sobre
 * `fecha_evento` (el escalar actual solo aporta vía su `fecha_ultimo_evento_*`,
 * nunca sin fecha). Cada campo se evalúa por su último registro propio: un
 * tacto fresco y un ternero vencido (o viceversa) no se arrastran entre sí.
 */
/**
 * Último registro con dato (preñez o ternero) cuya `fecha_evento` cae en
 * [desde, hasta] (YYYY-MM-DD), desempatando por `fecha_creacion`. SIN
 * vencimiento: la vigencia de 8 meses aplica solo al display del estado
 * actual — los índices se calculan por registros del período (KAN-219,
 * spec preñez v2 §2). El escalar actual solo aporta con su fecha de último
 * evento dentro de la ventana (anti-leak entre años).
 */
export function estadoPrenezEnVentana(
  animal: FertilityAnimalInput,
  desde: string,
  hasta: string
): EstadoPrenezVigente {
  let mejor: PregnancyHistoryEntry | null = null;
  let mejorDia: string | null = null;
  for (const entry of animal.prenies_historial ?? []) {
    if (!entry) continue;
    const dia = fechaSolo(entry.fecha_evento);
    if (dia === null || dia < desde || dia > hasta) continue;
    const tieneDato =
      isSet(entry.prenies) ||
      (entry.tiene_ternero !== undefined && entry.tiene_ternero !== null);
    if (!tieneDato) continue;
    const creada = entry.fecha_creacion ?? '';
    const mejorCreada = mejor?.fecha_creacion ?? '';
    if (mejorDia === null || dia > mejorDia || (dia === mejorDia && creada >= mejorCreada)) {
      mejor = entry;
      mejorDia = dia;
    }
  }

  if (mejor) {
    return {
      prenies: isSet(mejor.prenies) ? (mejor.prenies as string) : null,
      tiene_ternero: mejor.tiene_ternero === true,
    };
  }

  // Fallback escalar: solo si su fecha de último evento cae en la ventana
  const diaEscalar = fechaSolo(animal.fecha_ultimo_evento_prenies);
  if (
    isSet(animal.prenies) &&
    diaEscalar !== null &&
    diaEscalar >= desde &&
    diaEscalar <= hasta
  ) {
    return {
      prenies: animal.prenies as string,
      tiene_ternero: animal.tiene_ternero === true,
    };
  }

  // Fallback escalar de ternero (KAN-185): un ternero al pie sin historial
  // se atribuye al año de su fecha de último evento, nunca al corriente.
  const diaTernero = fechaSolo(animal.fecha_ultimo_evento_tiene_ternero);
  if (
    animal.tiene_ternero === true &&
    diaTernero !== null &&
    diaTernero >= desde &&
    diaTernero <= hasta
  ) {
    return { prenies: null, tiene_ternero: true };
  }

  return { prenies: null, tiene_ternero: false };
}

export function estadoPrenezVigente(
  animal: FertilityAnimalInput,
  hasta: string
): EstadoPrenezVigente {
  const hastaDia = fechaSolo(hasta);
  if (!hastaDia) return { prenies: null, tiene_ternero: false };

  const limite = fechaMenosMeses(hastaDia, MESES_VIGENCIA_PRENEZ);
  const historial = animal.prenies_historial ?? [];

  let prenies: string | null = null;
  let tieneTernero = false;

  const registroPrenies = ultimoRegistroConDato(historial, hastaDia, 'prenies');
  if (registroPrenies && (fechaSolo(registroPrenies.fecha_evento) as string) >= limite) {
    prenies = registroPrenies.prenies as string;
  } else {
    // Fallback escalar: solo con fecha de último evento dentro de la ventana.
    const diaEscalar = fechaSolo(animal.fecha_ultimo_evento_prenies);
    if (isSet(animal.prenies) && diaEscalar !== null && diaEscalar <= hastaDia && diaEscalar >= limite) {
      prenies = animal.prenies as string;
    }
  }

  const registroTernero = ultimoRegistroConDato(historial, hastaDia, 'ternero');
  if (registroTernero && (fechaSolo(registroTernero.fecha_evento) as string) >= limite) {
    tieneTernero = registroTernero.tiene_ternero === true;
  } else {
    const diaEscalar = fechaSolo(animal.fecha_ultimo_evento_tiene_ternero);
    if (
      animal.tiene_ternero === true &&
      diaEscalar !== null &&
      diaEscalar <= hastaDia &&
      diaEscalar >= limite
    ) {
      tieneTernero = true;
    }
  }

  return { prenies, tiene_ternero: tieneTernero };
}

/** Estado de preñez vigente a una fecha de corte (objeto con prenies y ternero). */
export interface EstadoPrenezAlCierre {
  prenies: string | null;
  tiene_ternero: boolean | null;
}

/**
 * Estado de preñez vigente a una fecha de corte `hasta` (YYYY-MM-DD):
 * generalización de `pregnancyStatusForYear` para períodos personalizados
 * (KAN-30/35/36).
 *
 * Devuelve el último registro con fecha de evento ≤ `hasta` (desempata por
 * fecha de creación). Si no hay registros ≤ `hasta`, el estado escalar actual
 * solo aplica si su fecha de último evento es ≤ `hasta` Y cae en el mismo año
 * que `hasta` — misma regla anti-leak que `pregnancyStatusForYear` (KAN-185):
 * el estado actual nunca se filtra a períodos pasados.
 * Devuelve null cuando no hay estado para esa fecha.
 */
export function pregnancyStatusAtDate(
  animal: FertilityAnimalInput,
  hasta: string
): EstadoPrenezAlCierre | null {
  const hastaDia = fechaSolo(hasta);
  if (!hastaDia) return null;

  const entries = (animal.prenies_historial ?? [])
    .filter(entry => {
      if (!entry) return false;
      const dia = fechaSolo(entry.fecha_evento);
      return dia !== null && dia <= hastaDia;
    })
    .sort((a, b) => {
      if (a.fecha_evento !== b.fecha_evento) {
        return a.fecha_evento < b.fecha_evento ? 1 : -1;
      }
      const aCreated = a.fecha_creacion ?? '';
      const bCreated = b.fecha_creacion ?? '';
      return aCreated < bCreated ? 1 : aCreated > bCreated ? -1 : 0;
    });

  if (entries.length > 0) {
    const last = entries[0];
    return {
      prenies: isSet(last.prenies) ? (last.prenies as string) : null,
      tiene_ternero: last.tiene_ternero === true ? true : null,
    };
  }

  const ultimoEvento = fechaSolo(animal.fecha_ultimo_evento_prenies);
  if (
    isSet(animal.prenies) &&
    ultimoEvento !== null &&
    ultimoEvento <= hastaDia &&
    ultimoEvento.slice(0, 4) === hastaDia.slice(0, 4)
  ) {
    return {
      prenies: animal.prenies as string,
      tiene_ternero: animal.tiene_ternero === true ? true : null,
    };
  }

  return null;
}

/**
 * Estado de preñez vigente al cierre de un año: el prenies del último registro
 * con fecha de evento ≤ año (desempata por fecha de creación). Si no hay
 * registros ≤ año, el estado actual solo aplica al año de su fecha de último
 * evento — nunca se filtra a años pasados (leak KAN-185).
 * Devuelve null cuando no hay estado para ese año.
 */
export function pregnancyStatusForYear(
  animal: FertilityAnimalInput,
  year: number
): string | null {
  const entries = (animal.prenies_historial ?? [])
    .filter(entry => {
      if (!entry) return false;
      const entryYear = yearOf(entry.fecha_evento);
      return entryYear !== null && entryYear <= year;
    })
    .sort((a, b) => {
      const aTime = new Date(a.fecha_evento).getTime();
      const bTime = new Date(b.fecha_evento).getTime();
      if (aTime !== bTime) return bTime - aTime;
      const aCreated = a.fecha_creacion ? new Date(a.fecha_creacion).getTime() : 0;
      const bCreated = b.fecha_creacion ? new Date(b.fecha_creacion).getTime() : 0;
      return bCreated - aCreated;
    });

  if (entries.length > 0) {
    return isSet(entries[0].prenies) ? (entries[0].prenies as string) : null;
  }

  if (isSet(animal.prenies) && yearOf(animal.fecha_ultimo_evento_prenies) === year) {
    return animal.prenies as string;
  }

  return null;
}
